import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPills, FaSearch, FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaUserMd, FaUserAlt, FaTag, FaArrowLeft, FaPaperclip } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import FileAttachment from '../components/FileAttachment';
import RecordCard from '../components/RecordCard';
import { getMedicines, createMedicine, updateMedicine, deleteMedicine, getPatients, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const EMPTY_FORM = { name: '', description: '', price: '', stock: '', category: '', manufacturer: '', expiryDate: '', patientId: '', patientName: '' };

const Pharmacy = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [meds, setMeds] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const fileRef = useRef();

  const buildFormData = (payload) => {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (editId) fd.append('existingAttachments', JSON.stringify(existingAttachments));
    selectedFiles.forEach(f => fd.append('files', f));
    return fd;
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCat) params.category = filterCat;
      const [medRes, patRes] = await Promise.allSettled([
        getMedicines(params),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
      ]);
      if (medRes.status === 'fulfilled') setMeds(medRes.value.data?.data || []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
    } catch { setMeds([]); }
    setLoading(false);
  }, [search, filterCat, role]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useAutoRefresh(fetchAll, 'pharmacy');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const base = {
        ...form,
        price: form.price === '' ? 0 : form.price,
        stock: form.stock === '' ? 0 : form.stock
      };
      const payload = (selectedFiles.length > 0 || editId) ? buildFormData(base) : base;
      if (editId) {
        const res = await updateMedicine(editId, payload);
        setMeds(prev => prev.map(m => m.id === editId ? (res.data?.data || m) : m));
        showToast('Medicine updated!', 'success');
      } else {
        const res = await createMedicine(payload);
        setMeds(prev => [res.data?.data, ...prev]);
        showToast('Medicine added!', 'success');
        triggerRefresh('pharmacy');
      }
      setShowModal(false); setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]);
    } catch (err) { showToast(err.message || 'Failed', 'error'); }
    setSaving(false);
  };

  const handleView = (item) => {
    setViewItem(item);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this medicine?')) return;
    try { await deleteMedicine(id); setMeds(prev => prev.filter(m => m.id !== id)); showToast('Deleted'); }
    catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const categories = [...new Set(meds.map(m => m.category).filter(Boolean))];
  const filteredMeds = meds.filter(m => !filterDate || (m.createdAt && m.createdAt.slice(0, 10) === filterDate));

  const statusColor = (stock) => {
    if (stock === 0) return { label: 'Out of Stock', bg: '#ffebee', color: '#c62828' };
    if (stock < 20) return { label: 'Low Stock', bg: '#fff3e0', color: '#e65100' };
    return { label: 'In Stock', bg: '#e8f5e9', color: '#2e7d32' };
  };

  if (showModal) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit Medicine' : 'Add Medicine'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaPills style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit Medicine' : 'Add Medicine'}</h1>
              <p>{editId ? 'Update medicine details' : 'Add a new medicine to pharmacy'}</p>
            </div>
            <button className="btn btn-outline" onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); }}>
              <FaArrowLeft /> Back
            </button>
          </div>
          <div className="card" style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Medicine Name *</label><input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Metformin 500mg" /></div>
              <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Medicine description..." /></div>
              {patients.length > 0 && (
                <div className="form-group">
                  <label>Assign to Patient (optional)</label>
                  <select value={form.patientId} onChange={e => {
                    const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                    const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                    setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '' }));
                  }}>
                    <option value="">General / No specific patient</option>
                    {patients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name} {p.userId ? '(Registered)' : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group"><label>Price (₹)</label><input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="0.00" /></div>
                <div className="form-group"><label>Stock</label><input type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))} placeholder="0" /></div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Category</label><input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Antibiotic" /></div>
                <div className="form-group"><label>Expiry Date</label><input type="month" value={form.expiryDate} onChange={e => setForm(p => ({ ...p, expiryDate: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Manufacturer</label><input value={form.manufacturer} onChange={e => setForm(p => ({ ...p, manufacturer: e.target.value }))} placeholder="Company name" /></div>
              <div className="form-group">
                <label><FaPaperclip style={{ marginRight: 6 }} />Attachments (PDF, Image, Word)</label>
                {existingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {existingAttachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} compact={true}
                        onRemove={() => setExistingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                  const files = Array.from(e.target.files);
                  if (files.some(f => f.size > MAX_ATTACHMENT_SIZE)) {
                    showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
                    e.target.value = '';
                    return;
                  }
                  setSelectedFiles(files);
                }} style={{ padding: '8px 0', fontSize: 13 }} />
                {selectedFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>{selectedFiles.map(f => f.name).join(', ')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add Medicine'}</button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Header title="Pharmacy" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaPills style={{ color: 'var(--primary)', marginRight: 8 }} />Pharmacy</h1>
            <p>{filteredMeds.length} {role === 'Patient' ? 'prescribed medicines' : 'medicines'}</p>
          </div>
          {(role === 'Doctor' || role === 'Admin') && (
            <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setShowModal(true); }}><FaPlus /> Add</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <FaSearch /><input placeholder="Search medicines..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {categories.length > 0 && (
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ padding: '10px 12px', border: '2px solid var(--border)', borderRadius: 10, fontSize: 13, background: 'white' }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c}>{c}</option>)}
            </select>
          )}
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            title="Filter by date added"
          />
          {filterDate && (
            <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: 12 }} onClick={() => setFilterDate('')}>
              Clear Filter
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filteredMeds.length === 0 ? (
          <div className="empty-state"><FaPills className="empty-state-icon" /><h3>No medicines found</h3><p>{role === 'Patient' ? 'No medicines prescribed yet' : 'Add medicines to your pharmacy'}</p></div>
        ) : (
          <div className="record-grid">
            {filteredMeds.map(med => {
              const st = statusColor(parseInt(med.stock) || 0);
              const meta = [];
              if (med.category) meta.push({ icon: <FaTag size={11} />, text: med.category });
              if (med.patientName) meta.push({ icon: <FaUserAlt size={11} />, text: `For: ${med.patientName}` });
              if (med.doctorName) meta.push({ icon: <FaUserMd size={11} />, text: `Dr. ${med.doctorName}` });
              const hasAttachments = (Array.isArray(med.attachments) && med.attachments.length > 0) || med.image_url;
              return (
                <RecordCard
                  key={med.id}
                  avatarLetter={(med.name || '?')[0].toUpperCase()}
                  title={med.name}
                  meta={meta}
                  subLabel={med.manufacturer || null}
                  items={[{
                    icon: <FaPills size={13} />,
                    name: `₹${med.price}`,
                    badges: [
                      <span key="st" style={{ color: st.color }}>{st.label}</span>,
                      `Stock: ${med.stock}`,
                      `Exp: ${med.expiryDate || 'N/A'}`,
                    ],
                  }]}
                  note={med.description}
                  onView={() => handleView(med)}
                  actions={(role === 'Doctor' || role === 'Admin') && (
                    <>
                      <button className="action-btn action-btn-edit" onClick={() => {
                        setForm({ name: med.name, description: med.description || '', price: med.price, stock: med.stock, category: med.category || '', manufacturer: med.manufacturer || '', expiryDate: med.expiryDate || '', patientId: med.patientId || '', patientName: med.patientName || '' });
                        setExistingAttachments(Array.isArray(med.attachments) && med.attachments.length > 0 ? med.attachments : (med.image_url ? [{ url: med.image_url, name: null }] : []));
                        setSelectedFiles([]);
                        setEditId(med.id); setShowModal(true);
                      }}><FaEdit /></button>
                      <button className="action-btn action-btn-delete" onClick={() => handleDelete(med.id)}><FaTrash /></button>
                    </>
                  )}
                >
                  {parseInt(med.stock) === 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#c62828' }}><FaExclamationTriangle /> Out of stock</div>}
                  {hasAttachments && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      {Array.isArray(med.attachments) && med.attachments.map((att, i) => (
                        <FileAttachment key={i} url={att.url} filename={att.name} compact={true} />
                      ))}
                      {(!Array.isArray(med.attachments) || med.attachments.length === 0) && med.image_url && (
                        <FileAttachment url={med.image_url} compact={true} />
                      )}
                    </div>
                  )}
                </RecordCard>
              );
            })}
          </div>
        )}
      </main>
      <BottomNav />

      {showViewModal && viewItem && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>Medicine Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Medicine Name</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{viewItem.name}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-medium)', marginTop: 4 }}>{viewItem.category || 'General'} · {viewItem.manufacturer || 'Unknown Manufacturer'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Price</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#2e7d32' }}>₹{viewItem.price}</div>
                </div>
              </div>

              {(viewItem.patientName || viewItem.doctorName) && (
                <div style={{ marginBottom: 15 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Prescription Info</div>
                  <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 10, borderLeft: '4px solid var(--primary)', display: 'flex', gap: 20 }}>
                    {viewItem.patientName && <div><strong style={{ color: 'var(--text-medium)' }}>For:</strong> {viewItem.patientName}</div>}
                    {viewItem.doctorName && <div><strong style={{ color: 'var(--text-medium)' }}>By:</strong> Dr. {viewItem.doctorName}</div>}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Inventory Info</div>
                <div style={{ background: '#e3f2fd', padding: 12, borderRadius: 10, display: 'flex', gap: 20 }}>
                  <div><strong style={{ color: 'var(--text-medium)' }}>Stock:</strong> <span style={{ color: parseInt(viewItem.stock) === 0 ? '#c62828' : 'inherit', fontWeight: parseInt(viewItem.stock) === 0 ? 'bold' : 'normal' }}>{viewItem.stock}</span></div>
                  <div><strong style={{ color: 'var(--text-medium)' }}>Expiry Date:</strong> {viewItem.expiryDate || 'N/A'}</div>
                </div>
              </div>

              {viewItem.description && (
                <div style={{ marginBottom: 15 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Description</div>
                  <div style={{ background: '#fff9c4', padding: 10, borderRadius: 8, fontSize: 14, color: '#5d4037', lineHeight: 1.5 }}>{viewItem.description}</div>
                </div>
              )}

              {((Array.isArray(viewItem.attachments) && viewItem.attachments.length > 0) || viewItem.image_url) && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Attachments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Array.isArray(viewItem.attachments) && viewItem.attachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} />
                    ))}
                    {!Array.isArray(viewItem.attachments) && viewItem.image_url && <FileAttachment url={viewItem.image_url} />}
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowViewModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pharmacy;
